import { useEffect, useRef } from 'react'

// Module-level flag: prevents other drawers from reacting to popstate
// events triggered by a drawer's cleanup history.back() call.
let isCleanupBack = false

// Module-level flag: set right before a programmatic forward navigation that
// will unmount open drawers (e.g. create-itinerary -> itinerary page). While it
// is true, drawers that are closing/unmounting as part of that navigation must
// NOT call history.back(). router.replace already consumes the drawer's synthetic
// history entry, so an extra back() only races and undoes the forward navigation
// — the faster the API resolves, the more likely back() fires mid-navigation.
let isNavigatingAway = false

/**
 * Call this immediately before a programmatic forward navigation (router.push /
 * router.replace) that will unmount open drawers. It suppresses the drawer history
 * cleanup back() for a short window so it cannot race the router navigation.
 */
export function suppressDrawerHistoryCleanup(durationMs = 1000) {
    isNavigatingAway = true
    window.setTimeout(() => {
        isNavigatingAway = false
    }, durationMs)
}

/**
 * Hook to handle browser back navigation for drawers in PWA
 *
 * When a drawer opens, it pushes a history entry so that:
 * - Browser back button closes the drawer instead of leaving the page
 * - Android hardware back button closes the drawer
 * - iOS swipe-back gesture closes the drawer
 *
 * Usage:
 * ```tsx
 * const [open, setOpen] = useState(false);
 * const { markHistoryForOpen, closeAndGoBack } = useDrawerBackBehavior({
 *   isOpen: open,
 *   onClose: () => setOpen(false),
 * });
 *
 * // When opening drawer
 * const handleOpen = () => {
 *   markHistoryForOpen();
 *   setOpen(true);
 * };
 *
 * // Use closeAndGoBack for onClose prop
 * <Drawer open={open} onClose={closeAndGoBack} />
 * ```
 */
export function useDrawerBackBehavior({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
    const pushedRef = useRef(false)
    const previousOpenRef = useRef(false) // Always start as false to catch initial open mounts
    const isBackNavigationRef = useRef(false)
    const isOpenRef = useRef(isOpen)
    const drawerIdRef = useRef<string>(Math.random().toString(36).substr(2, 9))

    isOpenRef.current = isOpen

    useEffect(() => {
        const onPop = (event: PopStateEvent) => {
            // Ignore popstate events triggered by another drawer's cleanup
            if (isCleanupBack) return

            // Only handle popstate if this drawer is open and has pushed history
            if (isOpen && pushedRef.current) {
                const state = event.state
                // If the state we navigated TO does NOT have our drawerId,
                // it means our history entry was popped (back pressed)
                if (!state || state.drawerId !== drawerIdRef.current) {
                    isBackNavigationRef.current = true
                    pushedRef.current = false
                    onClose()
                }
            }
        }

        window.addEventListener('popstate', onPop)
        return () => window.removeEventListener('popstate', onPop)
    }, [isOpen, onClose])

    // Auto-detect when drawer opens and mark history
    useEffect(() => {
        if (isOpen && !previousOpenRef.current && !pushedRef.current) {
            // Drawer just opened, mark history with unique drawer ID
            window.history.pushState({ drawer: true, drawerId: drawerIdRef.current }, '')
            pushedRef.current = true
        }
        previousOpenRef.current = isOpen
    }, [isOpen])

    // Clean up history entry when drawer closes (for non-back navigation closes)
    useEffect(() => {
        if (!isOpen && pushedRef.current && !isBackNavigationRef.current) {
            // Drawer closed manually (backdrop/cross/swipe), remove the history entry
            // Delay to let the close animation finish before calling history.back()
            const timeout = setTimeout(() => {
                pushedRef.current = false
                // A forward navigation is in progress (e.g. create-itin -> itinerary).
                // Skip back() so it can't race/undo the router navigation.
                if (isNavigatingAway) return
                // Only call history.back() if OUR entry is still the current one.
                // If another drawer opened on top, its pushState placed a new entry
                // and calling back() would pop that drawer's entry instead of ours.
                const currentState = window.history.state
                if (currentState?.drawerId === drawerIdRef.current) {
                    isCleanupBack = true
                    window.history.back()
                    // history.back() fires popstate asynchronously — use a longer
                    // delay so the flag stays true until the event is processed.
                    setTimeout(() => { isCleanupBack = false }, 50)
                }
            }, 300) // MUI drawer transition duration
            return () => clearTimeout(timeout)
        }

        // Reset the back navigation flag when drawer closes
        if (!isOpen) {
            isBackNavigationRef.current = false
        }
    }, [isOpen])

    // Cleanup on unmount only when the drawer is still open. If it was already closed
    // programmatically, the delayed close effect owns history cleanup — calling back()
    // here would race with forward navigation (e.g. RT PWA create-itin → itinerary).
    useEffect(() => {
        return () => {
            // A forward navigation is unmounting this drawer (e.g. create-itin ->
            // itinerary). The router owns the history transition; skip back().
            if (isNavigatingAway) return
            if (pushedRef.current && !isBackNavigationRef.current && isOpenRef.current) {
                const currentState = window.history.state
                if (currentState?.drawerId === drawerIdRef.current) {
                    isCleanupBack = true
                    window.history.back()
                    setTimeout(() => { isCleanupBack = false }, 50)
                }
            }
        }
    }, [])

    /**
     * Call this when opening the drawer.
     * IMPORTANT: Must be called inside the same click/tap event that opens the drawer
     * for iOS compatibility.
     */
    function markHistoryForOpen() {
        if (!pushedRef.current) {
            window.history.pushState({ drawer: true, drawerId: drawerIdRef.current }, '')
            pushedRef.current = true
        }
    }

    /**
     * Simple close function that just calls onClose.
     * The cleanup of history entry is handled automatically in useEffect.
     */
    function closeAndGoBack() {
        onClose()
    }

    return { markHistoryForOpen, closeAndGoBack }
}

export default useDrawerBackBehavior
